import type { Role } from '../rbac/roles.js'
import type { IdpAttributeMapping, IdpLastTest, LoginMethod } from './tenant-idp.types.js'

/**
 * How a tenant's users come into existence (TO-410 decision 3). Persisted as a
 * varchar + CHECK on `users_tenant_auth_configs.provisioning_mode`.
 */
export enum ProvisioningMode {
	/** Any first login through a usable provider is provisioned. */
	JIT = 'JIT',
	/** Nothing is provisioned at login; users are invited. The default. */
	INVITE = 'INVITE',
	/** Like `JIT`, but only for emails in `allowedEmailDomains`. */
	JIT_WITH_ALLOWLIST = 'JIT_WITH_ALLOWLIST',
	/** Users come from the tenant IdP's SCIM 2.0 client (T6 / TO-379); logins never create users. */
	SCIM = 'SCIM',
}

/** What a login whose org unit cannot be resolved does (TO-410 decision 7; applied from T4). */
export enum UnknownOrgUnitPolicy {
	REJECT_LOGIN = 'REJECT_LOGIN',
	NO_ORG_UNIT = 'NO_ORG_UNIT',
	DEFAULT_ORG_UNIT = 'DEFAULT_ORG_UNIT',
}

/** The federation protocol of a tenant identity provider. Both SAML2 and OIDC federate through Cognito (OIDC live since T5 / TO-378). */
export enum IdentityProviderProtocol {
	SAML2 = 'SAML2',
	OIDC = 'OIDC',
}

/** A provider is `VERIFIED` once a server-verified test login has passed. */
export enum IdentityProviderStatus {
	PENDING = 'PENDING',
	VERIFIED = 'VERIFIED',
}

/** Where a SAML provider's metadata came from (TO-410 design D4). */
export enum SamlMetadataSourceKind {
	/** Cognito fetches the document; TODAY never does. */
	URL = 'URL',
	/** An uploaded metadata document. */
	XML = 'XML',
	/** Edited field by field; TODAY generates the metadata document. */
	FIELDS = 'FIELDS',
}

/** The accepted SAML NameID formats. `emailAddress` is the default. */
export enum SamlNameIdFormat {
	EMAIL_ADDRESS = 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress',
	UNSPECIFIED = 'urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified',
	PERSISTENT = 'urn:oasis:names:tc:SAML:2.0:nameid-format:persistent',
	TRANSIENT = 'urn:oasis:names:tc:SAML:2.0:nameid-format:transient',
}

/** The upper bound on identity providers per tenant (`409 IDP_LIMIT_REACHED`). */
export const MAX_IDENTITY_PROVIDERS_PER_TENANT = 10

/** The SAML metadata values edited directly (the `FIELDS` source), or extracted from a document. */
export interface SamlProviderFields {
	entityId: string
	ssoUrl: string
	sloUrl?: string
	/** The IdP's signing certificate. Public by design; only its fingerprint and expiry are returned. */
	certificatePem: string
}

/**
 * The stored `configuration` of a SAML provider (design D4). Holds no secret:
 * SAML metadata is public. `metadataXml` is kept for the `XML` and `FIELDS`
 * sources so a rotation or restore can replay it to Cognito.
 */
export interface SamlProviderConfiguration {
	metadataSource: SamlMetadataSourceKind
	metadataUrl?: string
	metadataXml?: string
	/** Null for the `URL` source: Cognito fetches the document, so no field is known. */
	fields: SamlProviderFields | null
	nameIdFormat: SamlNameIdFormat
	signRequests: boolean
	/** Always true: Cognito always verifies the assertion signature. */
	requireSignedAssertions: true
	attributeMapping: IdpAttributeMapping
	/** The TO-308 mapping-guard input, when an explicit target group was requested. */
	targetGroup?: string
}

/**
 * The stored `configuration` of an OIDC provider. The client secret is NEVER
 * part of it: it lives in Secrets Manager, referenced by the row's
 * `secret_reference`, and is never returned.
 */
export interface OidcProviderConfiguration {
	issuer: string
	clientId: string
	authorizationEndpoint?: string
	tokenEndpoint?: string
	userinfoEndpoint?: string
	jwksUri?: string
	scopes: string[]
	attributeMapping: IdpAttributeMapping
}

/** A provider's stored `configuration`, discriminated by the row's `protocol`. */
export type IdentityProviderConfiguration = SamlProviderConfiguration | OidcProviderConfiguration

/** A tenant identity provider (TO-410 design D1). `isDefault` is derived from the config's pointer. */
export interface ITenantIdentityProvider {
	id: string
	tenantId: string
	code: string
	name: string
	protocol: IdentityProviderProtocol
	enabled: boolean
	status: IdentityProviderStatus
	configuration: IdentityProviderConfiguration
	/** The Secrets Manager ARN of an OIDC client secret. Never returned. */
	secretReference: string | null
	/** Set at creation for every new provider (`idp-<hex>`, either protocol since TO-378); null only on legacy rows migrated before TO-410. Globally unique. */
	cognitoProviderName: string | null
	lastTest: IdpLastTest | null
	rotatedAt: Date | null
	createdAt: Date
	createdBy: string | null
	updatedAt: Date
	updatedBy: string | null
}

/** The tenant-scoped roles a stored `defaultRole` may name (never `ORG_ADMIN`, TO-410 decision 1). */
export type AuthConfigDefaultRole = Role.ORG_USER | Role.ORG_MANAGER

/** A tenant's authentication config (TO-410 design D1). One row per tenant. */
export interface ITenantAuthConfig {
	tenantId: string
	loginEnabled: boolean
	localLoginEnabled: boolean
	/** TO-63 REQ-010/011: refuse federated sign-ins whose IdP did not assert MFA (default false). */
	requireIdpMfa: boolean
	/** TO-593 REQ-002: every user of the tenant must use MFA — the tenant mandate (default false). */
	requireMfa: boolean
	defaultProviderId: string | null
	provisioningMode: ProvisioningMode
	allowedEmailDomains: string[]
	defaultRole: AuthConfigDefaultRole | null
	unknownOrgUnitPolicy: UnknownOrgUnitPolicy
	/** Data only; no FK (org units live in another bounded context). */
	defaultOrgUnitId: string | null
	metadata: Record<string, unknown>
	createdAt: Date | null
	createdBy: string | null
	updatedAt: Date | null
	updatedBy: string | null
}

/**
 * The `AuthenticationConfig` resource of the admin surface (spec Contract):
 * the config without its tenant and create audit fields.
 */
export interface AuthenticationConfig {
	loginEnabled: boolean
	localLoginEnabled: boolean
	/** TO-63 REQ-010/011: refuse federated sign-ins whose IdP did not assert MFA (default false). */
	requireIdpMfa: boolean
	/** TO-593 REQ-002: every user of the tenant must use MFA — the tenant mandate (default false). */
	requireMfa: boolean
	defaultProviderId: string | null
	provisioningMode: ProvisioningMode
	allowedEmailDomains: string[]
	defaultRole: AuthConfigDefaultRole | null
	unknownOrgUnitPolicy: UnknownOrgUnitPolicy
	defaultOrgUnitId: string | null
	metadata: Record<string, unknown>
	updatedAt: string | null
	updatedBy: string | null
}

/** The public certificate summary of a SAML provider. The PEM itself is never returned. */
export interface SamlCertificateSummary {
	fingerprintSha256: string
	notAfter: string
}

/** The SAML `configuration` as returned (spec Contract). */
export interface SamlProviderConfigurationView {
	metadataSource: SamlMetadataSourceKind
	metadataUrl: string | null
	entityId: string | null
	ssoUrl: string | null
	sloUrl: string | null
	certificate: SamlCertificateSummary | null
	nameIdFormat: SamlNameIdFormat
	signRequests: boolean
	requireSignedAssertions: boolean
	attributeMapping: IdpAttributeMapping
}

/** The OIDC `configuration` as returned (spec Contract). Built from an allowlist: no secret, no reference. */
export interface OidcProviderConfigurationView {
	issuer: string
	clientId: string
	clientSecretSet: true
	authorizationEndpoint: string | null
	tokenEndpoint: string | null
	userinfoEndpoint: string | null
	jwksUri: string | null
	scopes: string[]
	attributeMapping: IdpAttributeMapping
}

/** The `IdentityProvider` resource of the admin surface (spec Contract). */
export interface IdentityProviderResource {
	id: string
	code: string
	name: string
	protocol: IdentityProviderProtocol
	enabled: boolean
	isDefault: boolean
	status: IdentityProviderStatus
	cognitoProviderName: string | null
	configuration: SamlProviderConfigurationView | OidcProviderConfigurationView
	lastTest: IdpLastTest | null
	createdAt: string
	updatedAt: string
	rotatedAt: string | null
}

/**
 * One entry of `GET /t/{slug}/login-options` (spec REQ-008). The native option
 * is `{ method: NATIVE, identityProvider: "COGNITO", providerId: null, name: null }`.
 */
export interface LoginOption {
	method: LoginMethod
	/** The Cognito `identity_provider` to send to the hosted UI. */
	identityProvider: string
	providerId: string | null
	name: string | null
}

/**
 * `GET /t/{slug}/login-options`. `brandId` (TO-541 REQ-103) is the tenant's stored
 * brand for an active, login-enabled tenant, else null — unknown, suspended and
 * login-disabled slugs answer exactly like a brand-less tenant.
 */
export interface TenantLoginOptionsResponseDTO {
	options: LoginOption[]
	brandId: string | null
}
